import React, { useEffect, useRef } from 'react';
import { Message, useMessage } from '@messageformat/react';
import { Action } from '@op/opds.opux';
import './Result.scss';
import CardTile from './CardTile';
import { Card, Age, ServicePlanId } from '../../common';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { cleanSessionStorage } from '../../util/commonUtil';
import { sendAdobeAnalyticsActionData, sendAdobeAnalyticsPhaseData, sendAdobeAnalyticsCommonActionData } from '../../util/analytics';
import { AnalyticsPhaseCategories, analyticsPhases } from './CardSelectorTool';
import { ServicePlan } from '../../domain/ServicePlan';
import { getLocale } from '../../util/commonUtil';

interface ResultProps {
  cardPoints: Map<Card, number>;
  age: Age;
  servicePlans: Map<ServicePlanId, ServicePlan>;
}

const Result = (props: ResultProps) => {
  const sessionStorage = useSessionStorage();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Duo is recommended if it has three or more points
  const shouldDuoBeRecommended = (): boolean => {
    const duoPoints = props.cardPoints.get(Card.DUO);
    if (duoPoints && duoPoints >= 3) {
      return true;
    } else {
      return false;
    }
  };

  // Return cards with points in descending order, without handling tie situations
  const getInitiallyOrderedCardsWithPoints = (cardPoints: Map<Card, number>): [Card, number][] => {
    cardPoints.delete(Card.DUO);
    const cardWithHighestPoints = [...cardPoints.entries()].reduce((prev, cur) => (cur[1] > prev[1] ? cur : prev));
    cardPoints.delete(cardWithHighestPoints[0]);
    const cardWithSecondHighestPoints = [...cardPoints.entries()].reduce((prev, cur) => (cur[1] > prev[1] ? cur : prev));
    cardPoints.delete(cardWithSecondHighestPoints[0]);
    const cardWithThirdHighestPoints = [...cardPoints.entries()].reduce((prev, cur) => (cur[1] > prev[1] ? cur : prev));
    cardPoints.delete(cardWithThirdHighestPoints[0]);
    const cardWithFourthHighestPoints = [...cardPoints.entries()].reduce((prev, cur) => (cur[1] > prev[1] ? cur : prev));
    cardPoints.delete(cardWithFourthHighestPoints[0]);
    return [cardWithHighestPoints, cardWithSecondHighestPoints, cardWithThirdHighestPoints, cardWithFourthHighestPoints];
  };

  const prioritizeCardInTieSituation = (cardsWithPoints: [Card, number][], cardToPrioritize: Card) => {
    const originalCardIndex = cardsWithPoints.findIndex((card) => card[0] === cardToPrioritize);
    const cardPoints = cardsWithPoints.find((card) => card[0] === cardToPrioritize)?.[1];
    cardsWithPoints.forEach((card, index) => {
      if (originalCardIndex > index && card[1] === cardPoints) {
        if (
          cardToPrioritize === Card.GOLD ||
          (cardToPrioritize === Card.PLATINUM && card[0] !== Card.GOLD) ||
          (cardToPrioritize === Card.CLASSIC && card[0] === Card.DEBIT)
        ) {
          const indexOfReplaceable = cardsWithPoints.findIndex((c) => c[0] === card[0]);
          cardsWithPoints[indexOfReplaceable] = cardsWithPoints[originalCardIndex];
          cardsWithPoints[originalCardIndex] = card;
        }
      }
    });
    return cardsWithPoints;
  };

  const prioritizeCardsInSpecialCases = (cardsWithPoints: [Card, number][]) => {
    let [cardWithHighestPoints, cardWithSecondHighestPoints, cardWithThirdHighestPoints, cardWithFourthHighestPoints] = cardsWithPoints;

    if (
      cardWithSecondHighestPoints[1] === cardWithThirdHighestPoints[1] &&
      cardWithSecondHighestPoints[1] === cardWithFourthHighestPoints[1] &&
      cardWithHighestPoints[0] === Card.DEBIT
    ) {
      const goldCard = cardsWithPoints.find((card) => card[0] === Card.GOLD);
      const classicCard = cardsWithPoints.find((card) => card[0] === Card.CLASSIC);
      const platinumCard = cardsWithPoints.find((card) => card[0] === Card.PLATINUM);

      cardsWithPoints = [cardWithHighestPoints, goldCard!, classicCard!, platinumCard!];
    }
    return cardsWithPoints;
  };

  // Priority: GOLD, PLATINUM, CLASSIC, DEBIT. But, there is a special case.
  const initRecommendations = (): Map<number, Card> => {
    const cardPoints = new Map<Card, number>(props.cardPoints);
    let cardsWithPoints = getInitiallyOrderedCardsWithPoints(cardPoints);
    cardsWithPoints = prioritizeCardInTieSituation(cardsWithPoints, Card.GOLD);
    cardsWithPoints = prioritizeCardInTieSituation(cardsWithPoints, Card.PLATINUM);
    cardsWithPoints = prioritizeCardInTieSituation(cardsWithPoints, Card.CLASSIC);
    cardsWithPoints = prioritizeCardsInSpecialCases(cardsWithPoints);
    const [cardWithHighestPoints, cardWithSecondHighestPoints, cardWithThirdHighestPoints] = cardsWithPoints;

    const recommendations = new Map([
      [1, cardWithHighestPoints[0]],
      [2, cardWithSecondHighestPoints[0]],
      [3, cardWithThirdHighestPoints[0]],
    ]);

    sessionStorage.updateItem('recommendations', JSON.stringify(Array.from(recommendations.entries())));

    return recommendations;
  };

  const getServicePlanByCard = (card: Card, duo: boolean = false): ServicePlan => {
    let planToReturn;
    switch (card) {
      case Card.CLASSIC:
        if (duo) planToReturn = props.servicePlans.get(ServicePlanId.OP_CLASSIC_PLUS_OP_DUO);
        else planToReturn = props.servicePlans.get(ServicePlanId.OP_CLASSIC);
        break;
      case Card.DEBIT:
        planToReturn = props.servicePlans.get(ServicePlanId.OP_DEBIT);
        break;
      case Card.GOLD:
        if (duo) planToReturn = props.servicePlans.get(ServicePlanId.OP_GOLD_PLUS_OP_DUO);
        else planToReturn = props.servicePlans.get(ServicePlanId.OP_GOLD);
        break;
      case Card.PLATINUM:
        planToReturn = props.servicePlans.get(ServicePlanId.OP_PLATINUM);
        break;
      default:
        if (duo) throw new Error(`Could not find service plan with cards ${card} and ${Card.DUO}.`);
        else throw new Error(`Could not find service plan with card ${card}.`);
    }
    if (!planToReturn) throw new Error('Service plan could not be found.');
    return planToReturn;
  };

  const locale = getLocale();
  const recommendDuo: boolean = shouldDuoBeRecommended();
  const recommendations: Map<number, Card> = initRecommendations();
  const classicImgUrl = getServicePlanByCard(Card.CLASSIC).productImageUrl;
  const classicPlusDuoImgUrl = getServicePlanByCard(Card.CLASSIC, true).productImageUrl;
  const debitImgUrl = getServicePlanByCard(Card.DEBIT).productImageUrl;
  const goldImgUrl = getServicePlanByCard(Card.GOLD).productImageUrl;
  const goldPlusDuoImgUrl = getServicePlanByCard(Card.GOLD, true).productImageUrl;
  const platinumImgUrl = getServicePlanByCard(Card.PLATINUM).productImageUrl;
  const classicTitleText = getServicePlanByCard(Card.CLASSIC).getTitle(locale);
  const classicDescriptionText = useMessage('paketinvalintatyokalu:tiili:visacreditdebit:kuvaus', '');
  const classicPlusDuoTitleText = getServicePlanByCard(Card.CLASSIC, true).getTitle(locale);
  const classicPlusDuoDescriptionText = useMessage('paketinvalintatyokalu:tiili:visacreditdebitplusduo:kuvaus', '');
  const debitTitleText = getServicePlanByCard(Card.DEBIT).getTitle(locale);
  const debitDescriptionText = useMessage('paketinvalintatyokalu:tiili:visadebit:kuvaus', '');
  const goldTitleText = getServicePlanByCard(Card.GOLD).getTitle(locale);
  const goldDescriptionText = useMessage('paketinvalintatyokalu:tiili:visagold:kuvaus', '');
  const goldPlusDuoTitleText = getServicePlanByCard(Card.GOLD, true).getTitle(locale);
  const goldPlusDuoDescriptionText = useMessage('paketinvalintatyokalu:tiili:visagoldplusduo:kuvaus', '');
  const platinumTitleText = getServicePlanByCard(Card.PLATINUM).getTitle(locale);
  const platinumDescriptionText = useMessage('paketinvalintatyokalu:tiili:visaplatinum:kuvaus', '');

  const resolvePrice = (card: Card, ownercustomer: boolean, duo: boolean = false): string => {
    switch (ownercustomer) {
      case true:
        return getServicePlanByCard(card, duo).getOwnerCustomerPrice(props.age);
      case false:
        return getServicePlanByCard(card, duo).getOtherCustomerPrice(props.age);
      default:
        throw new Error('No price configured for card.');
    }
  };

  const resolveDisclaimerText = () => {
    switch (props.age) {
      case Age.AGE_18_TO_25:
        return 'paketinvalintatyokalu:suositukset:disclaimer:18-25';
      case Age.AGE_26_TO_29:
        return 'paketinvalintatyokalu:suositukset:disclaimer:26-29';
      case Age.AGE_OVER_30:
        return 'paketinvalintatyokalu:suositukset:disclaimer:yli30';
      default:
        throw new Error('Error selecting age-specific disclaimer text.');
    }
  };

  const resolveScreenReaderDisclaimerText = () => {
    switch (props.age) {
      case Age.AGE_18_TO_25:
        return 'paketinvalintatyokalu:suositukset:disclaimer:18-25:sr';
      case Age.AGE_26_TO_29:
        return 'paketinvalintatyokalu:suositukset:disclaimer:26-29:sr';
      case Age.AGE_OVER_30:
        return 'paketinvalintatyokalu:suositukset:disclaimer:yli30'; // Same for screen reader
      default:
        throw new Error('Error selecting age-specific disclaimer screen reader text.');
    }
  };

  const resolveRecommendationForTile = (tileNumber: 1 | 2 | 3): [number, boolean] => {
    let resultNumber: 1 | 2 | 3;
    let duo: boolean;
    switch (tileNumber) {
      case 1:
        resultNumber = 1;
        duo = recommendDuo;
        break;
      case 2:
        if (recommendDuo && (recommendations.get(1) === Card.DEBIT || recommendations.get(1) === Card.PLATINUM)) {
          resultNumber = 2;
          duo = true;
        } else if (recommendDuo && recommendations.get(1) !== Card.DEBIT && recommendations.get(1) !== Card.PLATINUM) {
          resultNumber = 1;
          duo = false;
        } else {
          resultNumber = 2;
          duo = false;
        }
        break;
      case 3:
        if (recommendDuo && recommendations.get(1) !== Card.DEBIT && recommendations.get(1) !== Card.PLATINUM) {
          resultNumber = 2;
          duo = true;
        } else if (
          recommendDuo &&
          (recommendations.get(1) === Card.DEBIT || recommendations.get(1) === Card.PLATINUM) &&
          (recommendations.get(2) === Card.DEBIT || recommendations.get(2) === Card.PLATINUM)
        ) {
          resultNumber = 3;
          duo = recommendDuo;
        } else if (
          recommendDuo &&
          (recommendations.get(1) === Card.DEBIT || recommendations.get(1) === Card.PLATINUM) &&
          recommendDuo &&
          (recommendations.get(2) !== Card.DEBIT || recommendations.get(2) !== Card.PLATINUM)
        ) {
          resultNumber = 2;
          duo = false;
        } else {
          resultNumber = 3;
          duo = false;
        }
        break;
      default:
      // Will not execute
    }

    return [resultNumber!, duo!];
  };

  // Campaign ribbon is shown if service plan has campaign flag set to true within other customer, owner-customer or Owner Plus customer.
  // Ribbon is shown regardless of age.
  const isCampaign = (card: Card, showDuo: boolean): boolean => {
    const prices = getServicePlanByCard(card, showDuo).prices;
    const isOtherCustomerCampaign = prices.otherCustomer.some((otherCustomersPricesForAgeGroup) => otherCustomersPricesForAgeGroup.campaign);
    const isOwnerCustomerCampaign = prices.ownerCustomer.some((ownerCustomersPricesForAgeGroup) => ownerCustomersPricesForAgeGroup.campaign);
    const isOwnerPlusCampaign = prices.ownerPlus.some((ownerPlusPricesForAgeGroup) => ownerPlusPricesForAgeGroup.campaign);
    return (isOtherCustomerCampaign || isOwnerCustomerCampaign || isOwnerPlusCampaign) ?? false;
  };

  const resolveAnalyticsCommonAction = (): string | undefined => {
    try {
      let commonAction = 'element_view |';
      let card: Card;
      let duoSubStr: string;

      let [resultNumber, duo] = resolveRecommendationForTile(1);
      card = recommendations.get(resultNumber)!;
      duoSubStr = duo && card !== Card.PLATINUM && card !== Card.DEBIT ? ' & DUO' : '';
      commonAction += ` ${card}${duoSubStr} :`;

      [resultNumber, duo] = resolveRecommendationForTile(2);
      card = recommendations.get(resultNumber)!;
      duoSubStr = duo && card !== Card.PLATINUM && card !== Card.DEBIT ? ' & DUO' : '';
      commonAction += ` ${card}${duoSubStr} :`;

      [resultNumber, duo] = resolveRecommendationForTile(3);
      card = recommendations.get(resultNumber)!;
      duoSubStr = duo && card !== Card.PLATINUM && card !== Card.DEBIT ? ' & DUO' : '';
      commonAction += ` ${card}${duoSubStr}`;
      return commonAction;
    } catch {
      return undefined;
    }
  };

  const handleRetake = () => {
    cleanSessionStorage();
    sendAdobeAnalyticsActionData('Tee testi uudelleen', 'click', analyticsPhases.get('result')!);
  };

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const renderClassicTile = (primaryRecommendation: boolean, tileNumber: number, duo: boolean) => {
    return (
      <CardTile
        buttonHref={duo ? '/henkiloasiakkaat/paivittaiset/kortit/kaikki-kortit' : '/henkiloasiakkaat/paivittaiset/kortit/op-visa'}
        imageUrl={duo ? classicPlusDuoImgUrl : classicImgUrl}
        title={duo ? classicPlusDuoTitleText : classicTitleText}
        descriptionText={duo ? classicPlusDuoDescriptionText : classicDescriptionText}
        ownerCustomerPrice={resolvePrice(Card.CLASSIC, true, duo)}
        normalPrice={resolvePrice(Card.CLASSIC, false, duo)}
        primaryRecommendation={primaryRecommendation}
        tileNumber={tileNumber}
        duo={duo}
        showCampaignRibbon={isCampaign(Card.CLASSIC, duo)}
      />
    );
  };

  const renderDebitTile = (primaryRecommendation: boolean, tileNumber: number) => {
    return (
      <CardTile
        buttonHref="/henkiloasiakkaat/paivittaiset/kortit/op-visa-debit"
        imageUrl={debitImgUrl}
        title={debitTitleText}
        descriptionText={debitDescriptionText}
        ownerCustomerPrice={resolvePrice(Card.DEBIT, true)}
        normalPrice={resolvePrice(Card.DEBIT, false)}
        primaryRecommendation={primaryRecommendation}
        tileNumber={tileNumber}
        showCampaignRibbon={isCampaign(Card.DEBIT, false)}
      />
    );
  };

  const renderGoldTile = (primaryRecommendation: boolean, tileNumber: number, duo: boolean) => {
    return (
      <CardTile
        buttonHref={duo ? '/henkiloasiakkaat/paivittaiset/kortit/kaikki-kortit' : '/henkiloasiakkaat/paivittaiset/kortit/op-visa-gold'}
        imageUrl={duo ? goldPlusDuoImgUrl : goldImgUrl}
        title={duo ? goldPlusDuoTitleText : goldTitleText}
        descriptionText={duo ? goldPlusDuoDescriptionText : goldDescriptionText}
        ownerCustomerPrice={resolvePrice(Card.GOLD, true, duo)}
        normalPrice={resolvePrice(Card.GOLD, false, duo)}
        primaryRecommendation={primaryRecommendation}
        tileNumber={tileNumber}
        duo={duo}
        showCampaignRibbon={isCampaign(Card.GOLD, duo)}
      />
    );
  };

  const renderPlatinumTile = (primaryRecommendation: boolean, tileNumber: number) => {
    return (
      <CardTile
        buttonHref="/henkiloasiakkaat/paivittaiset/kortit/op-visa-platinum"
        imageUrl={platinumImgUrl}
        title={platinumTitleText}
        descriptionText={platinumDescriptionText}
        ownerCustomerPrice={resolvePrice(Card.PLATINUM, true)}
        normalPrice={resolvePrice(Card.PLATINUM, false)}
        primaryRecommendation={primaryRecommendation}
        tileNumber={tileNumber}
        showCampaignRibbon={isCampaign(Card.PLATINUM, false)}
      />
    );
  };

  const resolveResultToRender = (tileNumber: 1 | 2 | 3) => {
    const [resultNumber, duo] = resolveRecommendationForTile(tileNumber);

    switch (recommendations.get(resultNumber!)) {
      case Card.CLASSIC:
        return renderClassicTile(tileNumber === 1, tileNumber, duo);
      case Card.DEBIT:
        return renderDebitTile(tileNumber === 1, tileNumber);
      case Card.GOLD:
        return renderGoldTile(tileNumber === 1, tileNumber, duo);
      case Card.PLATINUM:
        return renderPlatinumTile(tileNumber === 1, tileNumber);
      default:
      // Will not execute
    }
  };

  sendAdobeAnalyticsPhaseData(AnalyticsPhaseCategories.COMPLETED, analyticsPhases.get('result')!);
  sendAdobeAnalyticsCommonActionData(resolveAnalyticsCommonAction(), 'view');

  return (
    <div>
      <h2 className="result-title" ref={headingRef} tabIndex={-1} data-testid="result-heading">
        <Message id="paketinvalintatyokalu:suositukset:otsikko" />
      </h2>

      <div className="result-results-wrapper">
        <div className="result-subhead-container result-subhead-container-main-recommendation">
          <h3 className="result-main-recommendation-subhead">
            <Message id="paketinvalintatyokalu:suositukset:alaotsikko:ensisijainen" />
          </h3>
        </div>
        {resolveResultToRender(1)}
        <div className="result-subhead-container result-subhead-container-middle">
          <h3>
            <Message id="paketinvalintatyokalu:suositukset:alaotsikko:toissijainen" />
          </h3>
        </div>
        {resolveResultToRender(2)}
        <div className="result-subhead-container result-subhead-container-right">
          <h3 className="result-hide" aria-hidden="true">
            <Message id="paketinvalintatyokalu:suositukset:alaotsikko:toissijainen" />
          </h3>
        </div>
        {resolveResultToRender(3)}
      </div>

      <h3 className="pac-screen-reader-only">
        <Message id="paketinvalintatyokalu:suositukset:disclaimer:title" />
      </h3>
      <p className="result-disclaimer">
        <span aria-hidden="true">
          <Message id={resolveDisclaimerText()} />
        </span>
        <span className="result-disclaimer-visually-hidden">
          <Message id={resolveScreenReaderDisclaimerText()} />
        </span>
      </p>

      <div className="result-end-actions">
        <Action size="medium" href={window.location.pathname} onClick={handleRetake}>
          <Message id="paketinvalintatyokalu:suositukset:toiminto:uudelleen" />
        </Action>
        <Action size="medium" href="TODO" className="result-remove">
          <Message id="paketinvalintatyokalu:suositukset:toiminto:vertaile" />
        </Action>
      </div>
    </div>
  );
};

export default Result;
